import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ArrowRight, Star, Volume2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useSpeech } from "@/hooks/useSpeech";
import rabbitImg from "@/assets/cartoon/rabbit.png";
import bearImg from "@/assets/cartoon/bear.png";
import catImg from "@/assets/cartoon/cat.png";
import AnimalSoundsGame from "@/components/games/cartoon/AnimalSoundsGame";
import ColoringGame from "@/components/games/cartoon/ColoringGame";
import MazeGame from "@/components/games/cartoon/MazeGame";
import CookingGame from "@/components/games/cartoon/CookingGame";

// ─── Types ───
interface GameDef {
  id: string;
  title: string;
  description: string;
  emoji: string;
  gradient: string;
  character: string;
  characterName: string;
}

// ─── Game definitions ───
const gameDefs: GameDef[] = [
  { id: "letters", title: "لعبة الحروف ✏️", description: "ساعد الأرنب يجمع الحروف!", emoji: "🔤", gradient: "from-emerald-400 to-teal-500", character: rabbitImg, characterName: "الأرنب أرنوب" },
  { id: "numbers", title: "لعبة الأرقام 🔢", description: "عدّ التفاح مع الدب!", emoji: "🍎", gradient: "from-blue-400 to-indigo-500", character: bearImg, characterName: "الدب دبدوب" },
  { id: "colors", title: "لعبة الألوان 🎨", description: "لوّن الرسمة مع القطة!", emoji: "🌈", gradient: "from-pink-400 to-rose-500", character: catImg, characterName: "القطة نونو" },
  { id: "matching", title: "مطابقة الصور 🃏", description: "اكتشف الأزواج المتشابهة!", emoji: "🧩", gradient: "from-purple-400 to-violet-500", character: rabbitImg, characterName: "الأرنب أرنوب" },
  { id: "puzzle", title: "ألغاز ذكية 🧠", description: "حل الألغاز الممتعة!", emoji: "💡", gradient: "from-amber-400 to-orange-500", character: bearImg, characterName: "الدب دبدوب" },
  { id: "words", title: "تكوين الكلمات 📖", description: "كوّن كلمات جديدة!", emoji: "📝", gradient: "from-cyan-400 to-blue-500", character: catImg, characterName: "القطة نونو" },
  { id: "animals", title: "الحيوانات وأصواتها 🦁", description: "اسمع الصوت واعرف الحيوان!", emoji: "🔊", gradient: "from-lime-400 to-green-500", character: bearImg, characterName: "الدب دبدوب" },
  { id: "coloring", title: "التلوين التفاعلي 🎨", description: "لوّن رسومات جميلة!", emoji: "🖌️", gradient: "from-fuchsia-400 to-pink-500", character: catImg, characterName: "القطة نونو" },
  { id: "maze", title: "لعبة المتاهة 🏃", description: "ساعد الحيوان يوصل لبيته!", emoji: "🗺️", gradient: "from-teal-400 to-cyan-500", character: rabbitImg, characterName: "الأرنب أرنوب" },
  { id: "cooking", title: "الطبخ الكرتوني 🍳", description: "اطبخ وصفات لذيذة!", emoji: "👨‍🍳", gradient: "from-orange-400 to-red-400", character: bearImg, characterName: "الدب دبدوب" },
];

// ─── Encouragement messages ───
const encouragements = [
  { text: "أحسنت يا بطل! 👏", emoji: "🌟" },
  { text: "ممتاز! أنت رائع! 💪", emoji: "🎉" },
  { text: "واصل! أنت نجم! ⭐", emoji: "🏆" },
  { text: "عظيم! كمّل كده! 🚀", emoji: "🎈" },
  { text: "برافو عليك! 🎊", emoji: "🥇" },
];

// ─── Letter game data ───
const arabicLetters = ["أ", "ب", "ت", "ث", "ج", "ح", "خ", "د", "ذ", "ر", "ز", "س", "ش", "ص", "ض", "ط", "ظ", "ع", "غ", "ف", "ق", "ك", "ل", "م", "ن", "هـ", "و", "ي"];
const letterWords: Record<string, string> = {
  "أ": "أرنب 🐰", "ب": "بطة 🦆", "ت": "تفاحة 🍎", "ث": "ثعلب 🦊", "ج": "جمل 🐪",
  "ح": "حصان 🐴", "خ": "خروف 🐑", "د": "ديك 🐓", "ذ": "ذرة 🌽", "ر": "رمان 🍅",
  "ز": "زرافة 🦒", "س": "سمكة 🐟", "ش": "شمس ☀️", "ص": "صقر 🦅", "ض": "ضفدع 🐸",
  "ط": "طائرة ✈️", "ظ": "ظرف ✉️", "ع": "عصفور 🐦", "غ": "غزال 🦌", "ف": "فراشة 🦋",
  "ق": "قطة 🐱", "ك": "كلب 🐕", "ل": "ليمون 🍋", "م": "موزة 🍌", "ن": "نحلة 🐝",
  "هـ": "هدهد 🐦", "و": "وردة 🌹", "ي": "يمامة 🕊️"
};

// ─── Number game data ───
const numberEmojis = ["🍎", "🍊", "🍋", "🍇", "🍓", "🍑", "🥝", "🍌", "🍉", "🫐"];

// ─── Matching game data ───
const matchPairs = [
  { emoji: "🐱", name: "قطة" }, { emoji: "🐶", name: "كلب" },
  { emoji: "☀️", name: "شمس" }, { emoji: "🌙", name: "قمر" },
  { emoji: "🌺", name: "وردة" }, { emoji: "🦋", name: "فراشة" },
  { emoji: "🐟", name: "سمكة" }, { emoji: "🐦", name: "عصفور" },
];

// ─── Puzzle data ───
const puzzles = [
  { q: "ما هو الحيوان الذي يقول مواء؟ 🐱", options: ["قطة", "كلب", "أرنب", "ديك"], answer: "قطة" },
  { q: "كم رجل للعنكبوت؟ 🕷️", options: ["4", "6", "8", "10"], answer: "8" },
  { q: "ما لون السماء؟ 🌤️", options: ["أحمر", "أخضر", "أزرق", "أصفر"], answer: "أزرق" },
  { q: "أين يعيش السمك؟ 🐠", options: ["الصحراء", "الغابة", "الماء", "الجبل"], answer: "الماء" },
  { q: "كم يوم في الأسبوع؟ 📅", options: ["5", "6", "7", "8"], answer: "7" },
  { q: "ما الفاكهة الصفراء؟ 🍌", options: ["تفاح", "موز", "عنب", "برتقال"], answer: "موز" },
  { q: "ما صوت القطة؟ 🐱", options: ["هاو", "مواء", "عوعو", "كوكو"], answer: "مواء" },
  { q: "كم فصل في السنة؟ 🍂", options: ["2", "3", "4", "5"], answer: "4" },
];

// ─── Color game data ───
const colorItems = [
  { emoji: "☀️", name: "شمس", color: "أصفر", colorClass: "bg-yellow-400" },
  { emoji: "🏠", name: "بيت", color: "أحمر", colorClass: "bg-red-400" },
  { emoji: "🌳", name: "شجرة", color: "أخضر", colorClass: "bg-green-400" },
  { emoji: "🌊", name: "بحر", color: "أزرق", colorClass: "bg-blue-400" },
  { emoji: "🍊", name: "برتقال", color: "برتقالي", colorClass: "bg-orange-400" },
  { emoji: "🍇", name: "عنب", color: "بنفسجي", colorClass: "bg-purple-400" },
];
const allColors = ["أصفر", "أحمر", "أخضر", "أزرق", "برتقالي", "بنفسجي"];

// ─── Word builder data ───
const wordBuilderData = [
  { word: "بيت", letters: ["ب", "ي", "ت"], extra: ["م", "ش"] },
  { word: "قطة", letters: ["ق", "ط", "ة"], extra: ["ل", "ن"] },
  { word: "كلب", letters: ["ك", "ل", "ب"], extra: ["و", "ر"] },
  { word: "ولد", letters: ["و", "ل", "د"], extra: ["ز", "ح"] },
  { word: "بنت", letters: ["ب", "ن", "ت"], extra: ["ف", "ع"] },
];

// ═══════════════════════════════════════
// Main Component
// ═══════════════════════════════════════
const CartoonGames = () => {
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const nurseryId = profile?.nursery_id;
  const { toast } = useToast();
  const { speak } = useSpeech();
  const qc = useQueryClient();

  const [activeGame, setActiveGame] = useState<string | null>(null);
  const [showEncouragement, setShowEncouragement] = useState<typeof encouragements[0] | null>(null);
  const [totalStars, setTotalStars] = useState(0);
  const [sessionStars, setSessionStars] = useState(0);

  // Game states
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);
  const [maxRounds, setMaxRounds] = useState(7);
  const [gameFinished, setGameFinished] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);

  // Letter game
  const [currentLetterIdx, setCurrentLetterIdx] = useState(0);
  // Number game
  const [targetNumber, setTargetNumber] = useState(3);
  const [numberOptions, setNumberOptions] = useState<number[]>([]);
  // Matching game
  const [matchCards, setMatchCards] = useState<{ emoji: string; id: number; flipped: boolean; matched: boolean }[]>([]);
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  const [matchedPairs, setMatchedPairs] = useState(0);
  // Puzzle game
  const [currentPuzzle, setCurrentPuzzle] = useState(0);
  const [usedPuzzles, setUsedPuzzles] = useState<number[]>([]);
  // Color game
  const [currentColorItem, setCurrentColorItem] = useState(0);
  // Word builder
  const [currentWord, setCurrentWord] = useState(0);
  const [builtLetters, setBuiltLetters] = useState<string[]>([]);
  const [availableLetters, setAvailableLetters] = useState<string[]>([]);

  // Fetch child
  const { data: child } = useQuery({
    queryKey: ["cartoon-child", childId],
    queryFn: async () => {
      const { data } = await supabase.from("children").select("*").eq("id", childId!).single();
      return data;
    },
    enabled: !!childId,
  });

  // Fetch existing stars
  const { data: gameResults } = useQuery({
    queryKey: ["cartoon-game-results", childId],
    queryFn: async () => {
      const { data } = await supabase
        .from("game_results")
        .select("stars")
        .eq("child_id", childId!);
      return data || [];
    },
    enabled: !!childId,
  });

  useEffect(() => {
    if (gameResults) {
      setTotalStars(gameResults.reduce((s, g) => s + (g.stars || 0), 0));
    }
  }, [gameResults]);

  const triggerEncouragement = useCallback((msg?: typeof encouragements[0]) => {
    const e = msg || encouragements[Math.floor(Math.random() * encouragements.length)];
    setShowEncouragement(e);
    speak(e.text.replace(/[👏💪⭐🚀🎊🌟🎉🏆🎈🥇]/g, ""));
    setTimeout(() => setShowEncouragement(null), 2500);
  }, [speak]);

  const saveResult = useCallback(async (gameType: string, sc: number, maxSc: number, dur: number) => {
    if (!childId || !nurseryId) return;
    const pct = Math.round((sc / maxSc) * 100);
    const stars = pct >= 90 ? 3 : pct >= 60 ? 2 : pct > 0 ? 1 : 0;
    setSessionStars(prev => prev + stars);
    await supabase.from("game_results").insert({
      child_id: childId,
      nursery_id: nurseryId,
      game_type: gameType,
      score: sc,
      max_score: maxSc,
      percentage: pct,
      stars,
      duration_seconds: dur,
    });
    qc.invalidateQueries({ queryKey: ["cartoon-game-results"] });
  }, [childId, nurseryId, qc]);

  // ─── Start Games ───
  const startLetterGame = () => {
    setActiveGame("letters");
    setCurrentLetterIdx(0);
    setScore(0);
    setRound(0);
    setMaxRounds(7);
    setGameFinished(false);
    setSelectedAnswer(null);
    setIsCorrect(null);
    generateLetterRound(0);
  };

  const generateLetterRound = (idx: number) => {
    setCurrentLetterIdx(idx % arabicLetters.length);
    setSelectedAnswer(null);
    setIsCorrect(null);
  };

  const startNumberGame = () => {
    setActiveGame("numbers");
    setScore(0);
    setRound(0);
    setMaxRounds(7);
    setGameFinished(false);
    generateNumberRound();
  };

  const generateNumberRound = () => {
    const num = Math.floor(Math.random() * 9) + 1;
    setTargetNumber(num);
    const opts = new Set<number>([num]);
    while (opts.size < 4) opts.add(Math.floor(Math.random() * 9) + 1);
    setNumberOptions([...opts].sort(() => Math.random() - 0.5));
    setSelectedAnswer(null);
    setIsCorrect(null);
  };

  const startColorGame = () => {
    setActiveGame("colors");
    setScore(0);
    setRound(0);
    setMaxRounds(colorItems.length);
    setGameFinished(false);
    setCurrentColorItem(0);
    setSelectedAnswer(null);
    setIsCorrect(null);
  };

  const startMatchingGame = () => {
    setActiveGame("matching");
    setScore(0);
    setGameFinished(false);
    setMatchedPairs(0);
    setFlippedCards([]);
    const selected = matchPairs.sort(() => Math.random() - 0.5).slice(0, 4);
    const cards = [...selected, ...selected]
      .sort(() => Math.random() - 0.5)
      .map((p, i) => ({ emoji: p.emoji, id: i, flipped: false, matched: false }));
    setMatchCards(cards);
  };

  const startPuzzleGame = () => {
    setActiveGame("puzzle");
    setScore(0);
    setRound(0);
    setMaxRounds(5);
    setGameFinished(false);
    setSelectedAnswer(null);
    setIsCorrect(null);
    const shuffled = puzzles.map((_, i) => i).sort(() => Math.random() - 0.5);
    setUsedPuzzles(shuffled);
    setCurrentPuzzle(shuffled[0]);
  };

  const startWordGame = () => {
    setActiveGame("words");
    setScore(0);
    setRound(0);
    setMaxRounds(wordBuilderData.length);
    setGameFinished(false);
    setCurrentWord(0);
    setBuiltLetters([]);
    const w = wordBuilderData[0];
    setAvailableLetters([...w.letters, ...w.extra].sort(() => Math.random() - 0.5));
  };

  const startNewGame = (id: string) => {
    setActiveGame(id);
    setScore(0);
    setRound(0);
    setGameFinished(false);
    setSelectedAnswer(null);
    setIsCorrect(null);
  };

  const handleNewGameFinish = (gameType: string) => (sc: number, maxSc: number) => {
    setScore(sc);
    setMaxRounds(maxSc);
    setGameFinished(true);
    saveResult(gameType, sc, maxSc, 0);
  };

  const startGame = (id: string) => {
    // New component-based games
    if (["animals", "coloring", "maze", "cooking"].includes(id)) {
      startNewGame(id);
      return;
    }
    const starters: Record<string, () => void> = {
      letters: startLetterGame, numbers: startNumberGame, colors: startColorGame,
      matching: startMatchingGame, puzzle: startPuzzleGame, words: startWordGame,
    };
    starters[id]?.();
  };

  // ─── Answer Handlers ───
  const handleLetterAnswer = (answer: string) => {
    const letter = arabicLetters[currentLetterIdx];
    const correct = answer === letterWords[letter];
    setSelectedAnswer(answer);
    setIsCorrect(correct);
    if (correct) {
      setScore(s => s + 1);
      triggerEncouragement();
    }
    setTimeout(() => {
      const nextRound = round + 1;
      if (nextRound >= maxRounds) {
        setGameFinished(true);
        saveResult("كرتون - الحروف", score + (correct ? 1 : 0), maxRounds, 0);
      } else {
        setRound(nextRound);
        generateLetterRound(currentLetterIdx + 1);
      }
    }, 1500);
  };

  const handleNumberAnswer = (answer: number) => {
    const correct = answer === targetNumber;
    setSelectedAnswer(String(answer));
    setIsCorrect(correct);
    if (correct) {
      setScore(s => s + 1);
      triggerEncouragement();
    }
    setTimeout(() => {
      const nextRound = round + 1;
      if (nextRound >= maxRounds) {
        setGameFinished(true);
        saveResult("كرتون - الأرقام", score + (correct ? 1 : 0), maxRounds, 0);
      } else {
        setRound(nextRound);
        generateNumberRound();
      }
    }, 1500);
  };

  const handleColorAnswer = (answer: string) => {
    const correct = answer === colorItems[currentColorItem].color;
    setSelectedAnswer(answer);
    setIsCorrect(correct);
    if (correct) {
      setScore(s => s + 1);
      triggerEncouragement();
    }
    setTimeout(() => {
      const nextRound = round + 1;
      if (nextRound >= maxRounds) {
        setGameFinished(true);
        saveResult("كرتون - الألوان", score + (correct ? 1 : 0), maxRounds, 0);
      } else {
        setRound(nextRound);
        setCurrentColorItem(nextRound % colorItems.length);
        setSelectedAnswer(null);
        setIsCorrect(null);
      }
    }, 1500);
  };

  const handleMatchCard = (idx: number) => {
    if (flippedCards.length >= 2 || matchCards[idx].matched || matchCards[idx].flipped) return;
    const newCards = [...matchCards];
    newCards[idx].flipped = true;
    setMatchCards(newCards);
    const newFlipped = [...flippedCards, idx];
    setFlippedCards(newFlipped);

    if (newFlipped.length === 2) {
      const [a, b] = newFlipped;
      if (matchCards[a].emoji === matchCards[b].emoji) {
        setTimeout(() => {
          const updated = [...newCards];
          updated[a].matched = true;
          updated[b].matched = true;
          setMatchCards(updated);
          setFlippedCards([]);
          const pairs = matchedPairs + 1;
          setMatchedPairs(pairs);
          setScore(s => s + 1);
          triggerEncouragement();
          if (pairs === 4) {
            setGameFinished(true);
            saveResult("كرتون - المطابقة", pairs, 4, 0);
          }
        }, 500);
      } else {
        setTimeout(() => {
          const updated = [...newCards];
          updated[a].flipped = false;
          updated[b].flipped = false;
          setMatchCards(updated);
          setFlippedCards([]);
        }, 800);
      }
    }
  };

  const handlePuzzleAnswer = (answer: string) => {
    const puzzle = puzzles[currentPuzzle];
    const correct = answer === puzzle.answer;
    setSelectedAnswer(answer);
    setIsCorrect(correct);
    if (correct) {
      setScore(s => s + 1);
      triggerEncouragement();
    }
    setTimeout(() => {
      const nextRound = round + 1;
      if (nextRound >= maxRounds) {
        setGameFinished(true);
        saveResult("كرتون - ألغاز", score + (correct ? 1 : 0), maxRounds, 0);
      } else {
        setRound(nextRound);
        setCurrentPuzzle(usedPuzzles[nextRound] || 0);
        setSelectedAnswer(null);
        setIsCorrect(null);
      }
    }, 1500);
  };

  const handleWordLetter = (letter: string) => {
    const w = wordBuilderData[currentWord];
    const expectedLetter = w.letters[builtLetters.length];
    if (letter === expectedLetter) {
      const newBuilt = [...builtLetters, letter];
      setBuiltLetters(newBuilt);
      setAvailableLetters(prev => {
        const idx = prev.indexOf(letter);
        return [...prev.slice(0, idx), ...prev.slice(idx + 1)];
      });
      if (newBuilt.length === w.letters.length) {
        setScore(s => s + 1);
        triggerEncouragement();
        setTimeout(() => {
          const nextRound = round + 1;
          if (nextRound >= maxRounds) {
            setGameFinished(true);
            saveResult("كرتون - الكلمات", score + 1, maxRounds, 0);
          } else {
            setRound(nextRound);
            setCurrentWord(nextRound);
            setBuiltLetters([]);
            const nw = wordBuilderData[nextRound];
            setAvailableLetters([...nw.letters, ...nw.extra].sort(() => Math.random() - 0.5));
          }
        }, 1200);
      }
    } else {
      speak("حاول مرة ثانية!");
    }
  };

  const getStarsForScore = (sc: number, max: number) => {
    const pct = (sc / max) * 100;
    return pct >= 90 ? 3 : pct >= 60 ? 2 : pct > 0 ? 1 : 0;
  };

  const gameDef = activeGame ? gameDefs.find(g => g.id === activeGame) : null;

  // ═══════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════
  return (
    <div className="min-h-screen relative overflow-hidden" dir="rtl">
      {/* Sky background */}
      <div className="fixed inset-0 -z-10 bg-gradient-to-b from-sky-300 via-sky-200 to-green-200">
        {/* Clouds */}
        <motion.div animate={{ x: [0, 30, 0] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-[8%] right-[10%] text-6xl opacity-60">☁️</motion.div>
        <motion.div animate={{ x: [0, -20, 0] }} transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-[15%] left-[15%] text-5xl opacity-50">☁️</motion.div>
        <motion.div animate={{ x: [0, 15, 0] }} transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-[5%] left-[50%] text-4xl opacity-40">☁️</motion.div>
        {/* Trees */}
        <div className="absolute bottom-0 right-[5%] text-6xl">🌳</div>
        <div className="absolute bottom-0 right-[20%] text-5xl">🌲</div>
        <div className="absolute bottom-0 left-[5%] text-6xl">🌳</div>
        <div className="absolute bottom-0 left-[25%] text-5xl">🌲</div>
        {/* Sun */}
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
          className="absolute top-[3%] left-[5%] text-7xl">☀️</motion.div>
      </div>

      {/* Encouragement overlay */}
      <AnimatePresence>
        {showEncouragement && (
          <motion.div
            initial={{ opacity: 0, scale: 0.3 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.3 }}
            className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none"
          >
            <motion.div
              animate={{ y: [0, -20, 0], rotate: [0, -3, 3, 0] }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-amber-300 via-yellow-300 to-amber-400 rounded-[2rem] p-8 shadow-2xl text-center border-4 border-white/50"
            >
              <p className="text-7xl mb-2">{showEncouragement.emoji}</p>
              <p className="text-2xl font-black text-amber-900">{showEncouragement.text}</p>
              {/* Balloons */}
              <div className="flex justify-center gap-2 mt-2">
                {["🎈", "🎈", "🎈"].map((b, i) => (
                  <motion.span key={i} animate={{ y: [-10, -30] }} transition={{ duration: 1, delay: i * 0.2, repeat: Infinity, repeatType: "reverse" }}
                    className="text-3xl">{b}</motion.span>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative z-10 p-4 sm:p-6 max-w-3xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="bg-white/80 hover:bg-white shadow-md rounded-xl"
            onClick={() => activeGame ? setActiveGame(null) : navigate(-1)}
          >
            <ArrowRight className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-black text-sky-900 drop-shadow-sm">
              {activeGame ? gameDef?.title : "🎮 ألعاب كرتونية"}
            </h1>
            {child && !activeGame && (
              <p className="text-sm text-sky-700 font-medium">مرحباً {child.full_name?.split(" ")[0]}! 🎉</p>
            )}
          </div>
          {/* Stars badge */}
          <Badge className="bg-amber-400 text-amber-900 border-amber-500 text-base px-3 py-1.5 shadow-md gap-1">
            <Star className="w-4 h-4 fill-amber-900" />
            {totalStars + sessionStars}
          </Badge>
        </div>

        {/* ═══ GAME MENU ═══ */}
        {!activeGame && (
          <div className="space-y-5">
            {/* Welcome characters */}
            <Card className="bg-white/80 backdrop-blur-sm border-2 border-white shadow-xl rounded-2xl overflow-hidden">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="flex -space-x-4 rtl:space-x-reverse">
                  <motion.img src={rabbitImg} alt="أرنب" className="w-16 h-16 rounded-full border-3 border-white shadow-md bg-emerald-50 object-cover"
                    animate={{ y: [0, -5, 0] }} transition={{ duration: 2, repeat: Infinity }} />
                  <motion.img src={bearImg} alt="دب" className="w-16 h-16 rounded-full border-3 border-white shadow-md bg-amber-50 object-cover"
                    animate={{ y: [0, -5, 0] }} transition={{ duration: 2, delay: 0.3, repeat: Infinity }} />
                  <motion.img src={catImg} alt="قطة" className="w-16 h-16 rounded-full border-3 border-white shadow-md bg-pink-50 object-cover"
                    animate={{ y: [0, -5, 0] }} transition={{ duration: 2, delay: 0.6, repeat: Infinity }} />
                </div>
                <div>
                  <p className="font-black text-lg text-foreground">أهلاً بك يا بطل! 🌈</p>
                  <p className="text-sm text-muted-foreground">اختر لعبة واستمتع بالتعلم!</p>
                </div>
              </CardContent>
            </Card>

            {/* Games grid */}
            <div className="grid grid-cols-2 gap-3">
              {gameDefs.map((game, i) => (
                <motion.div
                  key={game.id}
                  initial={{ opacity: 0, scale: 0.8, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                >
                  <Card
                    className="cursor-pointer hover:shadow-2xl transition-all hover:scale-105 overflow-hidden border-2 border-white/50 rounded-2xl"
                    onClick={() => startGame(game.id)}
                  >
                    <CardContent className={`p-0`}>
                      <div className={`bg-gradient-to-br ${game.gradient} p-4 text-center text-white relative`}>
                        <motion.img
                          src={game.character}
                          alt={game.characterName}
                          className="w-14 h-14 mx-auto rounded-full border-2 border-white/50 shadow-md bg-white/20 object-cover mb-2"
                          whileHover={{ scale: 1.1, rotate: 5 }}
                        />
                        <p className="text-3xl mb-1">{game.emoji}</p>
                        <h3 className="font-bold text-sm leading-tight">{game.title}</h3>
                      </div>
                      <div className="bg-white/90 p-2.5 text-center">
                        <p className="text-xs text-muted-foreground font-medium">{game.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* ═══ GAME FINISHED ═══ */}
        {gameFinished && gameDef && (
          <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}>
            <Card className="bg-white/90 backdrop-blur-sm border-2 border-amber-300 shadow-2xl rounded-3xl overflow-hidden">
              <CardContent className="p-6 text-center space-y-4">
                <motion.img
                  src={gameDef.character}
                  alt={gameDef.characterName}
                  className="w-24 h-24 mx-auto rounded-full border-4 border-amber-300 shadow-lg bg-amber-50 object-cover"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
                <h2 className="text-2xl font-black text-foreground">
                  {score === (activeGame === "matching" ? 4 : maxRounds) ? "🏆 أحسنت يا بطل!" : score > 0 ? "⭐ عمل رائع!" : "💪 حاول مرة ثانية!"}
                </h2>
                <p className="text-lg text-muted-foreground font-semibold">
                  {gameDef.characterName} يقول: أنت مبدع! 🎊
                </p>
                <div className="flex justify-center gap-2">
                  {Array.from({ length: getStarsForScore(score, activeGame === "matching" ? 4 : maxRounds) }).map((_, i) => (
                    <motion.div key={i} initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: i * 0.2, type: "spring" }}>
                      <Star className="w-10 h-10 fill-amber-400 text-amber-400 drop-shadow-md" />
                    </motion.div>
                  ))}
                </div>
                <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-lg px-5 py-2">
                  النتيجة: {score} / {activeGame === "matching" ? 4 : maxRounds}
                </Badge>
                <div className="flex gap-3 justify-center pt-2">
                  <Button onClick={() => startGame(activeGame!)} className="gap-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl px-6">
                    🔄 العب مرة ثانية
                  </Button>
                  <Button variant="outline" onClick={() => setActiveGame(null)} className="gap-2 rounded-xl px-6 border-2">
                    🎮 ألعاب أخرى
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* ═══ LETTER GAME ═══ */}
        {activeGame === "letters" && !gameFinished && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <Progress value={((round + 1) / maxRounds) * 100} className="h-3 rounded-full" />
            <Card className="bg-white/90 backdrop-blur-sm border-2 border-emerald-200 shadow-xl rounded-2xl">
              <CardContent className="p-5 space-y-4 text-center">
                <div className="flex items-center justify-center gap-3">
                  <img src={rabbitImg} alt="أرنوب" className="w-14 h-14 rounded-full border-2 border-emerald-300 bg-emerald-50 object-cover" />
                  <p className="text-sm font-semibold text-emerald-700">أرنوب يسألك: ما الكلمة التي تبدأ بحرف...</p>
                </div>
                <motion.div
                  key={currentLetterIdx}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="text-8xl font-black text-emerald-600 drop-shadow-md py-4"
                >
                  {arabicLetters[currentLetterIdx]}
                </motion.div>
                <div className="grid grid-cols-2 gap-3">
                  {(() => {
                    const letter = arabicLetters[currentLetterIdx];
                    const correct = letterWords[letter];
                    const others = Object.values(letterWords).filter(w => w !== correct).sort(() => Math.random() - 0.5).slice(0, 3);
                    const options = [correct, ...others].sort(() => Math.random() - 0.5);
                    return options.map((opt) => {
                      const isSelected = selectedAnswer === opt;
                      const isCorrectOpt = opt === correct;
                      let cls = "bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100";
                      if (selectedAnswer) {
                        if (isCorrectOpt) cls = "bg-green-200 border-green-400 text-green-900";
                        else if (isSelected) cls = "bg-red-200 border-red-400 text-red-900";
                      }
                      return (
                        <button key={opt} onClick={() => !selectedAnswer && handleLetterAnswer(opt)}
                          disabled={!!selectedAnswer}
                          className={`p-4 rounded-xl border-2 text-lg font-bold transition-all active:scale-95 ${cls}`}>
                          {opt}
                        </button>
                      );
                    });
                  })()}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* ═══ NUMBER GAME ═══ */}
        {activeGame === "numbers" && !gameFinished && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <Progress value={((round + 1) / maxRounds) * 100} className="h-3 rounded-full" />
            <Card className="bg-white/90 backdrop-blur-sm border-2 border-blue-200 shadow-xl rounded-2xl">
              <CardContent className="p-5 space-y-4 text-center">
                <div className="flex items-center justify-center gap-3">
                  <img src={bearImg} alt="دبدوب" className="w-14 h-14 rounded-full border-2 border-blue-300 bg-blue-50 object-cover" />
                  <p className="text-sm font-semibold text-blue-700">دبدوب يسألك: كم عدد التفاح؟ 🍎</p>
                </div>
                <motion.div
                  key={round}
                  initial={{ scale: 0.5 }}
                  animate={{ scale: 1 }}
                  className="flex flex-wrap justify-center gap-2 py-4"
                >
                  {Array.from({ length: targetNumber }).map((_, i) => (
                    <motion.span
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="text-5xl"
                    >
                      {numberEmojis[i % numberEmojis.length]}
                    </motion.span>
                  ))}
                </motion.div>
                <div className="grid grid-cols-2 gap-3">
                  {numberOptions.map((num) => {
                    const isSelected = selectedAnswer === String(num);
                    const isCorrectOpt = num === targetNumber;
                    let cls = "bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100";
                    if (selectedAnswer) {
                      if (isCorrectOpt) cls = "bg-green-200 border-green-400 text-green-900";
                      else if (isSelected) cls = "bg-red-200 border-red-400 text-red-900";
                    }
                    return (
                      <button key={num} onClick={() => !selectedAnswer && handleNumberAnswer(num)}
                        disabled={!!selectedAnswer}
                        className={`p-4 rounded-xl border-2 text-3xl font-black transition-all active:scale-95 ${cls}`}>
                        {num}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* ═══ COLOR GAME ═══ */}
        {activeGame === "colors" && !gameFinished && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <Progress value={((round + 1) / maxRounds) * 100} className="h-3 rounded-full" />
            <Card className="bg-white/90 backdrop-blur-sm border-2 border-pink-200 shadow-xl rounded-2xl">
              <CardContent className="p-5 space-y-4 text-center">
                <div className="flex items-center justify-center gap-3">
                  <img src={catImg} alt="نونو" className="w-14 h-14 rounded-full border-2 border-pink-300 bg-pink-50 object-cover" />
                  <p className="text-sm font-semibold text-pink-700">نونو تسألك: ما لون هذا الشيء؟</p>
                </div>
                <motion.div key={round} initial={{ scale: 0 }} animate={{ scale: 1 }} className="py-4">
                  <span className="text-8xl">{colorItems[currentColorItem].emoji}</span>
                  <p className="text-lg font-bold text-foreground mt-2">{colorItems[currentColorItem].name}</p>
                </motion.div>
                <div className="grid grid-cols-3 gap-2">
                  {allColors.map((color) => {
                    const isSelected = selectedAnswer === color;
                    const isCorrectOpt = color === colorItems[currentColorItem].color;
                    let cls = "bg-white border-gray-200 text-foreground hover:bg-gray-50";
                    if (selectedAnswer) {
                      if (isCorrectOpt) cls = "bg-green-200 border-green-400 text-green-900";
                      else if (isSelected) cls = "bg-red-200 border-red-400 text-red-900";
                    }
                    return (
                      <button key={color} onClick={() => !selectedAnswer && handleColorAnswer(color)}
                        disabled={!!selectedAnswer}
                        className={`p-3 rounded-xl border-2 text-sm font-bold transition-all active:scale-95 ${cls}`}>
                        <div className={`w-6 h-6 rounded-full mx-auto mb-1 ${colorItems.find(c => c.color === color)?.colorClass || "bg-gray-300"}`} />
                        {color}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* ═══ MATCHING GAME ═══ */}
        {activeGame === "matching" && !gameFinished && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <Badge className="bg-purple-100 text-purple-800 border-purple-300 text-sm">
              الأزواج: {matchedPairs} / 4
            </Badge>
            <Card className="bg-white/90 backdrop-blur-sm border-2 border-purple-200 shadow-xl rounded-2xl">
              <CardContent className="p-5">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <img src={rabbitImg} alt="أرنوب" className="w-12 h-12 rounded-full border-2 border-purple-300 bg-purple-50 object-cover" />
                  <p className="text-sm font-semibold text-purple-700">اكتشف الأزواج المتشابهة! 🃏</p>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {matchCards.map((card, idx) => (
                    <motion.button
                      key={idx}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleMatchCard(idx)}
                      className={`aspect-square rounded-xl border-2 text-3xl flex items-center justify-center transition-all ${
                        card.matched ? "bg-green-100 border-green-300" :
                        card.flipped ? "bg-purple-100 border-purple-300" :
                        "bg-purple-50 border-purple-200 hover:bg-purple-100"
                      }`}
                    >
                      {card.flipped || card.matched ? card.emoji : "❓"}
                    </motion.button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* ═══ PUZZLE GAME ═══ */}
        {activeGame === "puzzle" && !gameFinished && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <Progress value={((round + 1) / maxRounds) * 100} className="h-3 rounded-full" />
            <Card className="bg-white/90 backdrop-blur-sm border-2 border-amber-200 shadow-xl rounded-2xl">
              <CardContent className="p-5 space-y-4 text-center">
                <div className="flex items-center justify-center gap-3">
                  <img src={bearImg} alt="دبدوب" className="w-14 h-14 rounded-full border-2 border-amber-300 bg-amber-50 object-cover" />
                  <p className="text-sm font-semibold text-amber-700">دبدوب يسألك لغز!</p>
                </div>
                <motion.p key={round} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="text-lg font-bold text-foreground py-3">
                  {puzzles[currentPuzzle]?.q}
                </motion.p>
                <div className="grid grid-cols-2 gap-3">
                  {puzzles[currentPuzzle]?.options.map((opt) => {
                    const isSelected = selectedAnswer === opt;
                    const isCorrectOpt = opt === puzzles[currentPuzzle].answer;
                    let cls = "bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100";
                    if (selectedAnswer) {
                      if (isCorrectOpt) cls = "bg-green-200 border-green-400 text-green-900";
                      else if (isSelected) cls = "bg-red-200 border-red-400 text-red-900";
                    }
                    return (
                      <button key={opt} onClick={() => !selectedAnswer && handlePuzzleAnswer(opt)}
                        disabled={!!selectedAnswer}
                        className={`p-4 rounded-xl border-2 text-lg font-bold transition-all active:scale-95 ${cls}`}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* ═══ WORD BUILDER GAME ═══ */}
        {activeGame === "words" && !gameFinished && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <Progress value={((round + 1) / maxRounds) * 100} className="h-3 rounded-full" />
            <Card className="bg-white/90 backdrop-blur-sm border-2 border-cyan-200 shadow-xl rounded-2xl">
              <CardContent className="p-5 space-y-4 text-center">
                <div className="flex items-center justify-center gap-3">
                  <img src={catImg} alt="نونو" className="w-14 h-14 rounded-full border-2 border-cyan-300 bg-cyan-50 object-cover" />
                  <p className="text-sm font-semibold text-cyan-700">كوّن كلمة: {wordBuilderData[currentWord]?.word}</p>
                </div>
                {/* Built so far */}
                <div className="flex justify-center gap-2 py-3">
                  {wordBuilderData[currentWord]?.letters.map((_, i) => (
                    <div key={i} className={`w-14 h-14 rounded-xl border-3 flex items-center justify-center text-2xl font-black ${
                      builtLetters[i] ? "bg-cyan-100 border-cyan-400 text-cyan-800" : "bg-gray-100 border-dashed border-gray-300"
                    }`}>
                      {builtLetters[i] || ""}
                    </div>
                  ))}
                </div>
                {/* Available letters */}
                <div className="flex justify-center gap-2 flex-wrap">
                  {availableLetters.map((l, i) => (
                    <motion.button
                      key={`${l}-${i}`}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleWordLetter(l)}
                      className="w-12 h-12 rounded-xl bg-white border-2 border-cyan-200 text-xl font-bold text-foreground hover:bg-cyan-50 transition-all active:scale-95 shadow-sm"
                    >
                      {l}
                    </motion.button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* ═══ ANIMAL SOUNDS GAME ═══ */}
        {activeGame === "animals" && !gameFinished && gameDef && (
          <AnimalSoundsGame
            character={gameDef.character}
            characterName={gameDef.characterName}
            onFinish={handleNewGameFinish("كرتون - الحيوانات")}
            onCorrect={triggerEncouragement}
          />
        )}

        {/* ═══ COLORING GAME ═══ */}
        {activeGame === "coloring" && !gameFinished && gameDef && (
          <ColoringGame
            character={gameDef.character}
            characterName={gameDef.characterName}
            onFinish={handleNewGameFinish("كرتون - التلوين")}
            onCorrect={triggerEncouragement}
          />
        )}

        {/* ═══ MAZE GAME ═══ */}
        {activeGame === "maze" && !gameFinished && gameDef && (
          <MazeGame
            character={gameDef.character}
            characterName={gameDef.characterName}
            onFinish={handleNewGameFinish("كرتون - المتاهة")}
            onCorrect={triggerEncouragement}
          />
        )}

        {/* ═══ COOKING GAME ═══ */}
        {activeGame === "cooking" && !gameFinished && gameDef && (
          <CookingGame
            character={gameDef.character}
            characterName={gameDef.characterName}
            onFinish={handleNewGameFinish("كرتون - الطبخ")}
            onCorrect={triggerEncouragement}
          />
        )}
      </div>
    </div>
  );
};

export default CartoonGames;
